// ---------------------------------------------------------------------------
// PromptTree — Session-tabbed prompt tree with nested branching (CMS-01)
// ---------------------------------------------------------------------------
// Renders prompts as a visual tree: root prompts at the top level, with
// branch targets (prompts pointed to by an answer's next_prompt_id) indented
// under the answer that routes to them. Root-level prompts are drag-sortable
// via @dnd-kit; nested prompts move with their root parent.

import { useState, useMemo, useRef, useEffect } from 'react';
import { Plus, GripVertical, ArrowDown } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { PromptBlock } from './PromptBlock';
import { PromptEditor } from './PromptEditor';
import {
  useAddPrompt,
  useUpdatePrompt,
  useDeletePrompt,
  useReorderPrompts,
} from '@/hooks/use-templates';
import { useToast } from '@/components/ui/toast';
import type { TemplateSession, TemplatePrompt, PromptAnswerInput } from '@/types/template';

// Neutral pill color for branch connector badges — deliberately NOT the
// alert-level palette so they don't blend with answer rows.
const PILL_CLASS = 'bg-purple-100 text-purple-700 border border-purple-300';

// Pre-computed tree node — built in useMemo, rendered without side effects.
interface TreeNode {
  prompt: TemplatePrompt;
  depth: number;
  parentAnswerText?: string;
  children: TreeNode[];
}

interface PromptTreeProps {
  templateId: string;
  sessions: TemplateSession[];
  onAudioOpen: (prompt: TemplatePrompt) => void;
  onScrollAfterAdd?: () => void;
}

export function PromptTree({ templateId, sessions, onAudioOpen, onScrollAfterAdd }: PromptTreeProps) {
  const { toast } = useToast();
  const addPrompt = useAddPrompt(templateId);
  const updatePrompt = useUpdatePrompt(templateId);
  const deletePrompt = useDeletePrompt(templateId);
  const reorderPrompts = useReorderPrompts(templateId);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<TemplatePrompt | undefined>();
  const [addToSession, setAddToSession] = useState(1);
  const [suggestedSortOrder, setSuggestedSortOrder] = useState<number | undefined>();

  // Nesting context — set when adding a prompt via answer "+" or card "Add follow-up".
  // parentPrompt: the prompt we're nesting under
  // answerIds: which answer(s) to wire (single for answer "+", unrouted for follow-up)
  const [nestContext, setNestContext] = useState<{
    parentPrompt: TemplatePrompt;
    answerIds: string[];
    label: string;
  } | null>(null);

  const scrollAfterAdd = useRef(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const allPrompts = useMemo(
    () => sessions.flatMap((s) => s.prompts),
    [sessions],
  );

  useEffect(() => {
    if (scrollAfterAdd.current) {
      scrollAfterAdd.current = false;
      requestAnimationFrame(() => {
        if (onScrollAfterAdd) {
          onScrollAfterAdd();
        } else {
          bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      });
    }
  }, [allPrompts.length]);

  const promptById = useMemo(() => {
    const map = new Map<string, TemplatePrompt>();
    for (const p of allPrompts) map.set(p.prompt_id, p);
    return map;
  }, [allPrompts]);

  const sortedSessions = useMemo(
    () => [...sessions].sort((a, b) => a.sessionNumber - b.sessionNumber),
    [sessions],
  );
  const [activeSession, setActiveSession] = useState<string>(
    sortedSessions[0]?.sessionNumber.toString() ?? '1',
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // -------------------------------------------------------------------------
  // Tree structure computation
  // -------------------------------------------------------------------------

  // Set of prompt IDs that are explicit branch targets (pointed to by some
  // answer's next_prompt_id). These are rendered nested, not at root level.
  const branchTargetIds = useMemo(() => {
    const ids = new Set<string>();
    for (const p of allPrompts) {
      for (const a of p.answers) {
        if (a.next_prompt_id) ids.add(a.next_prompt_id);
      }
    }
    return ids;
  }, [allPrompts]);

  // Root prompts per session: prompts that are NOT branch targets.
  const rootPromptsBySession = useMemo(() => {
    const map = new Map<number, TemplatePrompt[]>();
    for (const s of sessions) {
      const roots = s.prompts
        .filter((p) => !branchTargetIds.has(p.prompt_id))
        .sort((a, b) => a.sort_order - b.sort_order);
      map.set(s.sessionNumber, roots);
    }
    return map;
  }, [sessions, branchTargetIds]);

  // Pre-compute tree structure as pure data so rendering has no side effects.
  // React Strict Mode double-renders — a mutable visited Set during render
  // causes the second pass to see all IDs already visited and return null.
  const treeBySession = useMemo(() => {
    const map = new Map<number, TreeNode[]>();

    function buildNode(
      prompt: TemplatePrompt,
      depth: number,
      visited: Set<string>,
      parentAnswerText?: string,
    ): TreeNode | null {
      if (visited.has(prompt.prompt_id)) return null;
      visited.add(prompt.prompt_id);

      const children: TreeNode[] = [];
      const sortedAnswers = [...prompt.answers].sort(
        (a, b) => a.sort_order - b.sort_order,
      );

      // Detect when all answers route to the same prompt — show "Always"
      // instead of a single answer's text.
      const routedIds = sortedAnswers
        .filter((a) => a.next_prompt_id)
        .map((a) => a.next_prompt_id!);
      const allSameTarget =
        prompt.answers.length > 1 &&
        routedIds.length === prompt.answers.length &&
        routedIds.every((id) => id === routedIds[0]);

      const seen = new Set<string>();
      for (const answer of sortedAnswers) {
        if (answer.next_prompt_id && !seen.has(answer.next_prompt_id)) {
          seen.add(answer.next_prompt_id);
          const child = promptById.get(answer.next_prompt_id);
          if (child) {
            const pillText = allSameTarget ? 'Always' : answer.answer_text;
            const childNode = buildNode(child, depth + 1, visited, pillText);
            if (childNode) children.push(childNode);
          }
        }
      }

      return { prompt, depth, parentAnswerText, children };
    }

    for (const [sessionNum, roots] of rootPromptsBySession) {
      const visited = new Set<string>();
      const nodes: TreeNode[] = [];
      for (const root of roots) {
        const node = buildNode(root, 0, visited);
        if (node) nodes.push(node);
      }
      map.set(sessionNum, nodes);
    }
    return map;
  }, [promptById, rootPromptsBySession]);

  // Reachable-set (unchanged from before — used for "Unreachable" warning)
  const reachableIds = useMemo(() => {
    const ids = new Set<string>();
    for (const p of allPrompts) {
      if (p.is_root) ids.add(p.prompt_id);
    }
    for (const p of allPrompts) {
      for (const a of p.answers) {
        if (a.next_prompt_id) ids.add(a.next_prompt_id);
      }
    }
    const bySession = new Map<number, TemplatePrompt[]>();
    for (const p of allPrompts) {
      const list = bySession.get(p.session_number) ?? [];
      list.push(p);
      bySession.set(p.session_number, list);
    }
    for (const sessionPrompts of bySession.values()) {
      const sorted = [...sessionPrompts].sort((a, b) => a.sort_order - b.sort_order);
      for (let i = 0; i < sorted.length - 1; i++) {
        const current = sorted[i];
        const hasExplicitRouting = current.answers.some((a) => a.next_prompt_id);
        if (!hasExplicitRouting) {
          ids.add(sorted[i + 1].prompt_id);
        }
      }
    }
    return ids;
  }, [allPrompts]);

  const promptLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of allPrompts) {
      const text = p.prompt_text ?? '';
      const truncated = text.length > 40 ? `${text.slice(0, 40)}…` : text;
      const label = truncated
        ? `#${p.sort_order} — ${truncated}`
        : `#${p.sort_order} · S${p.session_number}`;
      map.set(p.prompt_id, label);
    }
    return map;
  }, [allPrompts]);

  // -------------------------------------------------------------------------
  // Handlers (unchanged)
  // -------------------------------------------------------------------------

  function handleAddPrompt(sessionNumber: number) {
    setEditingPrompt(undefined);
    setNestContext(null);
    setSuggestedSortOrder(undefined);
    setAddToSession(sessionNumber);
    setEditorOpen(true);
  }

  function handleEditPrompt(prompt: TemplatePrompt) {
    setEditingPrompt(prompt);
    setSuggestedSortOrder(undefined);
    setAddToSession(prompt.session_number);
    if (branchTargetIds.has(prompt.prompt_id)) {
      setNestContext({ parentPrompt: prompt, answerIds: [], label: 'follow-up (no further branching)' });
    } else {
      setNestContext(null);
    }
    setEditorOpen(true);
  }

  function handleAddNested(parentPrompt: TemplatePrompt, answerId: string) {
    if (branchTargetIds.has(parentPrompt.prompt_id)) {
      toast({ title: 'Follow-up questions cannot have further branching', variant: 'default' });
      return;
    }
    const answer = parentPrompt.answers.find((a) => a.prompt_answer_id === answerId);
    setEditingPrompt(undefined);
    setNestContext({
      parentPrompt,
      answerIds: [answerId],
      label: answer ? `"${answer.answer_text}" on #${parentPrompt.sort_order}` : `#${parentPrompt.sort_order}`,
    });
    setAddToSession(parentPrompt.session_number);
    setEditorOpen(true);
  }

  async function handleDeletePrompt(promptId: string) {
    try {
      const isRoot = !branchTargetIds.has(promptId);
      const idsToDelete: string[] = [];

      if (isRoot) {
        const prompt = promptById.get(promptId);
        if (prompt) {
          for (const a of prompt.answers) {
            if (a.next_prompt_id) idsToDelete.push(a.next_prompt_id);
          }
        }
      }
      idsToDelete.push(promptId);

      for (const id of idsToDelete) {
        await deletePrompt.mutateAsync(id);
      }

      toast({
        title: idsToDelete.length > 1
          ? `Prompt and ${idsToDelete.length - 1} follow-up(s) deleted`
          : 'Prompt deleted',
        variant: 'success',
      });
    } catch {
      toast({ title: 'Failed to delete prompt', variant: 'destructive' });
    }
  }

  async function handleSavePrompt(data: {
    isInformational: boolean;
    questionId?: number;
    informationalTextId?: number;
    promptText?: string;
    sortOrder: number;
    sessionNumber: number;
    answers: PromptAnswerInput[];
  }) {
    try {
      if (editingPrompt) {
        await updatePrompt.mutateAsync({
          promptId: editingPrompt.prompt_id,
          sortOrder: data.sortOrder,
          sessionNumber: data.sessionNumber,
          promptText: data.promptText,
          answers: data.answers,
        });
        toast({ title: 'Prompt updated', variant: 'success' });
      } else {
        const created = await addPrompt.mutateAsync({
          questionId: data.questionId,
          informationalTextId: data.informationalTextId,
          sortOrder: data.sortOrder,
          isInformational: data.isInformational,
          sessionNumber: data.sessionNumber,
          promptText: data.promptText,
          answers: data.answers,
        });

        // Wire parent answer(s) to the new prompt if nesting
        if (nestContext && created?.prompt_id) {
          const parent = nestContext.parentPrompt;
          const wiredAnswers: PromptAnswerInput[] = parent.answers.map((a) => ({
            answerText: a.answer_text,
            alertLevel: a.alert_level,
            keypress: a.keypress ?? undefined,
            sortOrder: a.sort_order,
            nextPromptId: nestContext.answerIds.includes(a.prompt_answer_id)
              ? created.prompt_id
              : a.next_prompt_id ?? undefined,
          }));

          await updatePrompt.mutateAsync({
            promptId: parent.prompt_id,
            sortOrder: parent.sort_order,
            sessionNumber: parent.session_number,
            answers: wiredAnswers,
          });
        }

        toast({ title: 'Prompt added', variant: 'success' });
        scrollAfterAdd.current = true;
        setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }), 150);
      }
      setEditorOpen(false);
      setNestContext(null);
    } catch {
      toast({ title: 'Failed to save prompt', variant: 'destructive' });
    }
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const sessionNum = Number(activeSession);
    const rootPrompts = rootPromptsBySession.get(sessionNum) ?? [];

    const oldIndex = rootPrompts.findIndex((p) => p.prompt_id === active.id);
    const newIndex = rootPrompts.findIndex((p) => p.prompt_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    // Build the new root order, then flatten all prompts (root + nested)
    // back into a single sort_order sequence for the backend.
    const reordered = [...rootPrompts];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    // Flatten: for each root prompt, collect it + its subtree in order
    const flatOrder: string[] = [];
    const visited = new Set<string>();

    function collectSubtree(prompt: TemplatePrompt) {
      if (visited.has(prompt.prompt_id)) return;
      visited.add(prompt.prompt_id);
      flatOrder.push(prompt.prompt_id);
      for (const a of [...prompt.answers].sort((x, y) => x.sort_order - y.sort_order)) {
        if (a.next_prompt_id) {
          const child = promptById.get(a.next_prompt_id);
          if (child && child.session_number === sessionNum) {
            collectSubtree(child);
          }
        }
      }
    }

    for (const root of reordered) {
      collectSubtree(root);
    }

    // Include any session prompts not yet visited (orphans)
    const sessionPrompts = allPrompts
      .filter((p) => p.session_number === sessionNum)
      .sort((a, b) => a.sort_order - b.sort_order);
    for (const p of sessionPrompts) {
      if (!visited.has(p.prompt_id)) flatOrder.push(p.prompt_id);
    }

    try {
      await reorderPrompts.mutateAsync(flatOrder);
      toast({ title: 'Prompts reordered', variant: 'success' });
    } catch {
      toast({ title: 'Failed to reorder prompts', variant: 'destructive' });
    }
  }

  // -------------------------------------------------------------------------
  // Pure renderer — reads pre-computed TreeNode data, no mutable state.
  // -------------------------------------------------------------------------

  function renderTreeNode(node: TreeNode): React.ReactNode {
    return (
      <div key={node.prompt.prompt_id}>
        {node.parentAnswerText && node.depth > 0 && (
          <div
            className="flex items-center gap-1.5 mb-1 mt-2"
            style={{ marginLeft: `${node.depth * 32}px` }}
          >
            <Badge className={`text-[11px] px-2 py-0 ${PILL_CLASS}`}>
              {node.parentAnswerText}
            </Badge>
            <ArrowDown className="h-3 w-3 text-carbon-400" />
          </div>
        )}

        <div style={{ marginLeft: `${node.depth * 32}px` }}>
          <PromptBlock
            prompt={node.prompt}
            depth={node.depth}
            isReachable={reachableIds.has(node.prompt.prompt_id)}
            promptLabels={promptLabels}
            onEdit={handleEditPrompt}
            onDelete={handleDeletePrompt}
            onAudio={onAudioOpen}
            onAddNested={handleAddNested}
          />
        </div>

        {node.children.map((child) => renderTreeNode(child))}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const nextSession = sessions.length > 0
    ? Math.max(...sessions.map((s) => s.sessionNumber)) + 1
    : 1;

  if (sortedSessions.length === 0) {
    return (
      <div className="space-y-3">
        <div className="rounded-ds-008 border border-silver-200 bg-white p-8 text-center">
          <p className="text-sm text-carbon-500">
            No prompts yet. Add a session to get started.
          </p>
        </div>
        <Button
          variant="secondary"
          className="w-full"
          onClick={() => handleAddPrompt(1)}
        >
          <Plus className="h-4 w-4 mr-1" /> Add Session 1
        </Button>
        {editorOpen && (
          <PromptEditor
            open={editorOpen}
            onOpenChange={(v) => { setEditorOpen(v); if (!v) { setNestContext(null); setSuggestedSortOrder(undefined); } }}
            prompt={editingPrompt}
            sessionNumber={addToSession}
            existingPrompts={allPrompts}
            promptLabels={promptLabels}
            nestLabel={nestContext?.label}
            suggestedSortOrder={suggestedSortOrder}
            onSave={handleSavePrompt}
            isPending={addPrompt.isPending || updatePrompt.isPending}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Tabs value={activeSession} onValueChange={setActiveSession}>
        <TabsList>
          {sortedSessions.map((s) => (
            <TabsTrigger key={s.sessionNumber} value={s.sessionNumber.toString()}>
              Session {s.sessionNumber}
              <span className="ml-1.5 text-[10px] font-normal text-carbon-400">
                ({s.prompts.length})
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        {sortedSessions.map((session) => {
          const roots = rootPromptsBySession.get(session.sessionNumber) ?? [];
          const rootIds = roots.map((p) => p.prompt_id);

          return (
            <TabsContent
              key={session.sessionNumber}
              value={session.sessionNumber.toString()}
              className="mt-3"
            >
              <div className="space-y-2">
                {roots.length === 0 && session.prompts.length === 0 ? (
                  <div className="rounded-ds-008 border border-silver-200 bg-white p-6 text-center text-sm text-carbon-500">
                    No prompts in this session yet.
                  </div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext items={rootIds} strategy={verticalListSortingStrategy}>
                      {(treeBySession.get(session.sessionNumber) ?? []).map(
                        (rootNode) => (
                          <SortablePromptGroup
                            key={rootNode.prompt.prompt_id}
                            rootPrompt={rootNode.prompt}
                            renderSubtree={() => renderTreeNode(rootNode)}
                          />
                        ),
                      )}
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Spacer so the last card isn't hidden behind the floating bar */}
      <div ref={bottomRef} className="h-4" />

      {/* Floating action bar — always visible at bottom */}
      <div className="fixed bottom-6 right-6 z-40 flex items-center gap-3">
        <Button
          variant="secondary"
          className="h-12 gap-2 rounded-full bg-white px-5 shadow-lg"
          onClick={() => handleAddPrompt(nextSession)}
        >
          <Plus className="h-4 w-4" /> Add Session {nextSession}
        </Button>
        <Button
          className="h-12 gap-2 rounded-full px-5 shadow-lg"
          onClick={() => handleAddPrompt(Number(activeSession))}
        >
          <Plus className="h-5 w-5" /> Add Prompt
        </Button>
      </div>

      {editorOpen && (
        <PromptEditor
          open={editorOpen}
          onOpenChange={(v) => { setEditorOpen(v); if (!v) { setNestContext(null); setSuggestedSortOrder(undefined); } }}
          prompt={editingPrompt}
          sessionNumber={addToSession}
          existingPrompts={allPrompts}
          promptLabels={promptLabels}
          nestLabel={nestContext?.label}
          onSave={handleSavePrompt}
          isPending={addPrompt.isPending || updatePrompt.isPending}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SortablePromptGroup — wraps a root prompt + its nested subtree for DnD
// ---------------------------------------------------------------------------

interface SortablePromptGroupProps {
  rootPrompt: TemplatePrompt;
  renderSubtree: () => React.ReactNode;
}

function SortablePromptGroup({ rootPrompt, renderSubtree }: SortablePromptGroupProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rootPrompt.prompt_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative' as const,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-stretch gap-0">
      <button
        type="button"
        className="flex items-start pt-4 px-1 cursor-grab active:cursor-grabbing text-silver-400 hover:text-carbon-500 touch-none shrink-0"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">
        {renderSubtree()}
      </div>
    </div>
  );
}
